import {
  AggregationIcon,
  FormControlSizes,
  MixpanelEvent,
  SearchSelect,
  useAutocompleteGroupSorter,
  useMixpanel,
  type SearchSelectProps,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useMarketSelectorContext } from '../MarketSelectorContext';
import {
  GROUP_AGGREGATIONS,
  GROUP_DEFAULT_AGGREGATION,
  presetRowEntriesRepresentSameSelection,
  type PresetRowEntry,
} from '../presets/presetListModel';
import { useMarketSelectorPresets } from '../presets/useMarketSelectorPresets';

const MOBILE_AGGREGATION_GROUP_ORDER = [GROUP_DEFAULT_AGGREGATION, GROUP_AGGREGATIONS];

export function MobileMarketSelectorAggregationSelect() {
  const { disableAggregations } = useMarketSelectorContext();
  const { marketSelectorAPI, customPresetItemsByID, listEntries, selectedListEntry, getLabel, showNoAggregations } =
    useMarketSelectorPresets();
  const mixpanel = useMixpanel();
  const groupSorter = useAutocompleteGroupSorter(MOBILE_AGGREGATION_GROUP_ORDER);

  const options = useMemo(() => {
    const customEntries = listEntries.filter(entry => entry.kind === 'custom');
    const defaultAggregationEntries = listEntries.filter(
      entry => entry.kind === 'aggregation' && entry.placement === 'defaultPin'
    );
    const aggregationEntries = listEntries.filter(entry => entry.kind === 'aggregation' && entry.placement === 'list');

    return [...customEntries, ...defaultAggregationEntries, ...aggregationEntries];
  }, [listEntries]);

  const getGroup = useCallback((entry: PresetRowEntry) => {
    if (entry.kind === 'custom') {
      return undefined;
    }

    return entry.placement === 'defaultPin' ? GROUP_DEFAULT_AGGREGATION : GROUP_AGGREGATIONS;
  }, []);

  const handleChange = useCallback<NonNullable<SearchSelectProps<PresetRowEntry>['onChange']>>(
    entry => {
      if (!entry) {
        return;
      }

      const customPresetItem = customPresetItemsByID.get(entry.presetId);
      if (customPresetItem) {
        customPresetItem.onClick(marketSelectorAPI);
        return;
      }

      marketSelectorAPI.selectPreset(entry.presetId);
      mixpanel.track(MixpanelEvent.SelectAggregation);
    },
    [customPresetItemsByID, marketSelectorAPI, mixpanel]
  );

  if (disableAggregations || showNoAggregations || options.length === 0) {
    return null;
  }

  return (
    <SearchSelect<PresetRowEntry>
      prefix={<AggregationIcon />}
      options={options}
      selection={selectedListEntry ?? undefined}
      onChange={handleChange}
      getLabel={getLabel}
      getGroup={getGroup}
      groupSorter={groupSorter}
      equalityChecker={(a, b) => b != null && presetRowEntriesRepresentSameSelection(a, b)}
      initialSortByLabel={false}
      placeholder="Select aggregation..."
      size={FormControlSizes.Default}
      portalize
      w="100%"
      dataTestID="mobile-market-selector-aggregation-select"
    />
  );
}
